/**
 * "Apps" tab of the "Your agent" panel: every app of the catalog with its connection
 * state, one card per app (replaces the former sidebar page /agent-studio/apps, which
 * stays reachable by link). A connected app with skills opens its window (skills by
 * sub-app with their live data, and for Wix its knowledge; PackageLibrary.js AppView);
 * every other app the detail modal of that page (connect / manage); webhook apps
 * (Zapier, Make, n8n) put a prepared request into Mo's input instead.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faKey } from '@fortawesome/free-solid-svg-icons';
import { AppDetailModal, AppIcon, CredentialsModal, useIntegrationProviders } from '../../pages/AppsPage';
import { PROVIDER_META } from '../providerMeta';
import { Spinner } from './ui';

export const DRAFT_EVENT = 'agentStudio:draft';

export default function AppsTab({ botId, packages, onOpenLibrary }) {
  const { t } = useTranslation();
  const { providers, loading, refresh } = useIntegrationProviders(botId);
  const [active, setActive] = useState(null);
  const [creds, setCreds] = useState(false);
  const [showSoon, setShowSoon] = useState(false);

  const templates = useMemo(() => {
    const n = {};
    for (const p of packages || []) {
      const c = n[p.provider] || (n[p.provider] = { total: 0, active: 0 });
      c.total += 1;
      if (p.installed) c.active += 1;
    }
    return n;
  }, [packages]);

  const connected = providers.filter((p) => p.connected && !p.coming_soon);
  const available = providers.filter((p) => !p.connected && !p.coming_soon);
  const soon = providers.filter((p) => p.coming_soon);

  const close = () => {
    setActive(null);
    refresh();
  };
  // webhook apps: a prepared request in Mo's input (BuildView's dock listens)
  const webhookBuild = (label) => {
    try { localStorage.setItem(`agentStudio.draft.${botId}`, t('agent_studio_webhook_draft', { app: label })); } catch (_) { /* storage unavailable */ }
    window.dispatchEvent(new Event(DRAFT_EVENT));
    setActive(null);
  };

  // one card per app (Robert, 17.10.2026): a connected app with skills opens its window (skills, data, knowledge),
  // every other app the connect / manage detail
  const row = (app) => {
    const label = (PROVIDER_META[app.key] && PROVIDER_META[app.key].label) || app.label;
    const count = templates[app.key];
    const openApp = app.connected && count ? () => onOpenLibrary(app.key) : () => setActive(app);
    return (
      <li key={app.key} className="as-app-row">
        <button type="button" className="as-app-main" disabled={!!app.coming_soon}
          onClick={app.coming_soon ? undefined : openApp}>
          <span className="as-app-logo"><AppIcon app={app} /></span>
          <span className="as-app-text">
            <span className="as-app-name">{label}</span>
            <span className="as-app-sub">
              {app.connected && count ? t('agent_studio_apps_active', { n: count.active, total: count.total }) : app.summary}
            </span>
          </span>
          {app.coming_soon
            ? <span className="as-badge as-badge-grey">{t('apps_coming_soon', 'Coming soon')}</span>
            : (
              <>
                {app.connected && <span className="as-badge as-badge-green">{t('agent_studio_apps_connected')}</span>}
                <FontAwesomeIcon icon={faChevronRight} className="as-app-go" />
              </>
            )}
        </button>
      </li>
    );
  };

  return (
    <div className="as-panel-body">
      <div className="as-panel-hint-row">
        <p className="as-panel-hint">{t('agent_studio_apps_hint')}</p>
        <button type="button" className="as-btn-portal" onClick={() => setCreds(true)}>
          <FontAwesomeIcon icon={faKey} /> {t('cred_manage_button')}
        </button>
      </div>
      {loading ? <Spinner /> : (
        <>
          {connected.length > 0 && (
            <section className="as-app-group">
              <div className="as-panel-subhead">{t('agent_studio_apps_connected_title')}</div>
              <ul className="as-app-list">{connected.map(row)}</ul>
            </section>
          )}
          <section className="as-app-group">
            <div className="as-panel-subhead">{t('agent_studio_apps_available_title')}</div>
            <ul className="as-app-list">{available.map(row)}</ul>
          </section>
          {soon.length > 0 && (
            <section className="as-app-group">
              <button type="button" className="as-btn-link" onClick={() => setShowSoon((v) => !v)}>
                {t('agent_studio_apps_soon', { count: soon.length })}
              </button>
              {showSoon && <ul className="as-app-list">{soon.map(row)}</ul>}
            </section>
          )}
        </>
      )}
      {active && <AppDetailModal app={active} botId={botId} onClose={close} onWebhookBuild={webhookBuild} />}
      {creds && <CredentialsModal onClose={() => setCreds(false)} />}
    </div>
  );
}
